import { z } from 'zod';

/**
 * What a leave application must look like before it can be saved.
 *
 * Mirrors the backend's `staffLeave.validation.ts` — the server is the
 * authority, these rules only save a round trip. Keep the two in step.
 *
 * Two rules are deliberately left to the server: whether the dates fall inside
 * the academic year, and whether the entitlement covers them. Neither the
 * session's ends nor the institute's quota are in the browser, and guessing
 * either here would refuse a valid application.
 */

export const STAFF_LEAVE_TYPES = ['CASUAL', 'SICK', 'PRIVILEGE'] as const;

/** The `YYYY-MM-DD` a `<input type="date">` holds, and the API takes. */
const day = z
  .string()
  .trim()
  .min(1, 'Pick a date')
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-15');

export const staffLeaveSchema = z
  .object({
    type: z.enum(STAFF_LEAVE_TYPES, {
      errorMap: () => ({ message: 'Choose what kind of leave this is' }),
    }),
    startDate: day,
    endDate: day,
    days: z
      .number({ invalid_type_error: 'Say how many days this is' })
      .positive('A leave of no days is not a leave')
      .max(366, 'That is longer than an academic year')
      // Half days are granted; quarter days are not.
      .refine((value) => Number.isInteger(value * 2), {
        message: 'Leave is granted in half days — 1, 1.5, 2 and so on',
      }),
    reason: z
      .string()
      .trim()
      .min(3, 'Say briefly why the leave is needed')
      .max(250, 'Keep the reason under 250 characters'),
  })
  .refine((value) => value.endDate >= value.startDate, {
    message: 'The last day cannot be before the first',
    path: ['endDate'],
  });

/** What the form holds while it is being filled in. */
export interface StaffLeaveFormValues {
  type: (typeof STAFF_LEAVE_TYPES)[number];
  startDate: string;
  endDate: string;
  /** A string while typing — an empty number input holds "", not 0. */
  days: string;
  reason: string;
}

/** What the API takes, once parsed. */
export type StaffLeavePayload = z.infer<typeof staffLeaveSchema>;

/** A new application opens as a single day of casual leave — the commonest. */
export const EMPTY_STAFF_LEAVE_FORM: StaffLeaveFormValues = {
  type: 'CASUAL',
  startDate: '',
  endDate: '',
  days: '0.5',
  reason: '',
};
